import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Mail, ArrowLeft, PhoneCall } from "lucide-react";
import { Navbar, Footer } from "@/components/Chrome";
import { api, API, money2, num, STATUS_LABEL, STATUS_COLOR } from "@/lib/spott";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function QuoteDetail() {
  const { quoteId } = useParams();
  const { user } = useAuth();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = `Cotización ${quoteId} — Spott`;
    api.get(`/quotes/${quoteId}`)
      .then(({ data }) => { setQ(data); setEmail(data.contact_email || ""); })
      .catch((e) => setErr(e?.response?.data?.detail || "No se pudo cargar la cotización"));
  }, [quoteId]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/quotes/send-email", { quote_id: quoteId, email });
      toast.success(`Cotización enviada a ${email}`);
      setModal(false);
      const { data } = await api.get(`/quotes/${quoteId}`);
      setQ(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar");
    } finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-spott-light">
      <Navbar />
      <div className="mx-auto max-w-lg px-5 py-28 text-center">
        <h1 className="text-xl font-bold text-spott-blue">{err}</h1>
        <Link to="/portal" className="mt-6 inline-block rounded-full bg-spott-yellow px-6 py-3 text-sm font-bold text-spott-blue">
          Volver al portal
        </Link>
      </div>
    </div>
  );

  if (!q) return (
    <div className="min-h-screen bg-spott-light">
      <Navbar />
      <div className="flex h-[60vh] items-center justify-center">
        <div className="h-9 w-9 animate-spin rounded-full border-2 border-spott-yellow border-t-transparent" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-spott-light">
      <Navbar />
      <div className="mx-auto max-w-4xl px-5 py-12 lg:px-10" data-testid="quote-detail">
        <Link to={user?.role === "admin" || user?.role === "agente" ? "/admin" : "/portal"}
          className="inline-flex items-center gap-2 text-sm font-medium text-spott-blue">
          <ArrowLeft size={15} /> Volver
        </Link>

        <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="font-mono text-xs text-gray-500">{q.quote_id}</span>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl">
              {q.company_name || "Cotización Spott"}
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              {q.industry || "—"} · {q.duration_months} mes(es) · creada el {q.created_at?.slice(0, 10)}
            </p>
          </div>
          <span className={`rounded-full px-4 py-1.5 text-xs font-semibold ${STATUS_COLOR[q.status]}`}
            data-testid="quote-status">{STATUS_LABEL[q.status]}</span>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            { l: "Total campaña (USD)", v: money2(q.total) },
            { l: "Costo por mes", v: money2(q.monthly_cost) },
            { l: "Impactos estimados", v: num(q.total_reach) },
          ].map((k) => (
            <div key={k.l} className="rounded-xl border border-spott-mid bg-white p-6">
              <div className="text-xs text-gray-500">{k.l}</div>
              <div className="mt-2 text-xl font-bold text-spott-blue">{k.v}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 overflow-hidden rounded-xl border border-spott-mid bg-white">
          <div className="bg-spott-blue px-6 py-4 text-sm font-semibold text-white">Espacios publicitarios</div>
          {q.space_items?.map((i) => (
            <div key={i.space_id} className="flex items-center justify-between gap-4 border-b border-spott-mid px-6 py-4">
              <div>
                <div className="text-sm font-semibold text-spott-blue">{i.name}</div>
                <div className="text-xs text-gray-500">{i.retailer_name} · {i.city} · {i.format}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-spott-blue">{money2(i.total)}</div>
                <div className="text-xs text-gray-500">{i.units || 1} × {money2(i.unit_price)}/mes</div>
              </div>
            </div>
          ))}
          {q.service_items?.length > 0 && (
            <>
              <div className="bg-spott-blue px-6 py-4 text-sm font-semibold text-white">Servicios adicionales</div>
              {q.service_items.map((i) => (
                <div key={i.service_id} className="flex items-center justify-between border-b border-spott-mid px-6 py-4">
                  <div className="text-sm font-semibold text-spott-blue">{i.name}</div>
                  <div className="text-sm font-bold text-spott-blue">{money2(i.total)}</div>
                </div>
              ))}
            </>
          )}
          <div className="space-y-2 px-6 py-5 text-sm">
            <div className="flex justify-between text-gray-600"><span>Subtotal espacios</span><span>{money2(q.spaces_subtotal)}</span></div>
            <div className="flex justify-between text-gray-600"><span>Subtotal servicios</span><span>{money2(q.services_subtotal)}</span></div>
            {q.discount_amount > 0 && (
              <div className="flex justify-between font-medium text-emerald-600">
                <span>Descuento (hasta {q.discount_percent}%)</span><span>- {money2(q.discount_amount)}</span>
              </div>
            )}
            {q.service_fee > 0 && (
              <div className="flex justify-between font-medium text-spott-blue" data-testid="detail-fee">
                <span>{q.service_fee_label || "Fee de servicio Spott"} ({q.service_fee_percent}%)</span>
                <span>{money2(q.service_fee)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between bg-spott-yellow px-6 py-5">
            <span className="text-sm font-bold text-spott-blue">TOTAL (USD)</span>
            <span className="text-2xl font-bold text-spott-blue">{money2(q.total)}</span>
          </div>
        </div>

        <div className="mt-8 rounded-xl border border-spott-mid bg-white p-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-spott-blue">Brief de campaña</h2>
          <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-2">
            {[
              ["Objetivos", (q.objectives || []).join(", ") || "—"],
              ["Ciudades", (q.cities || []).join(", ") || "—"],
              ["NSE", (q.nse || []).join(", ") || "—"],
              ["Tipo de retailer", (q.retailer_types || []).join(", ") || "—"],
              ["Edad", (q.age_ranges || []).join(", ") || "—"],
              ["Contacto", `${q.contact_name || "—"} · ${q.contact_email || "—"}`],
              ["Presupuesto referencial", q.budget_reference ? money2(q.budget_reference) : "—"],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-xs text-gray-500">{k}</dt>
                <dd className="mt-1 font-medium text-spott-blue">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        {q.notes_log?.length > 0 && (
          <div className="mt-6 rounded-xl border border-spott-mid bg-white p-6">
            <h2 className="text-sm font-bold uppercase tracking-wide text-spott-blue">Notas de seguimiento</h2>
            <ul className="mt-4 space-y-3">
              {q.notes_log.map((n, i) => (
                <li key={i} className="border-l-2 border-spott-yellow pl-4 text-sm text-gray-600">
                  {n.note}
                  <span className="block text-xs text-gray-400">{n.actor} · {n.created_at?.slice(0, 16).replace("T", " ")}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <a href={`${API}/quotes/${q.quote_id}/pdf`} target="_blank" rel="noreferrer" data-testid="detail-pdf-btn"
            className="inline-flex items-center gap-2 rounded-full bg-spott-blue px-6 py-3.5 text-sm font-bold text-white">
            <Download size={16} /> Descargar PDF
          </a>
          <button onClick={() => setModal(true)} data-testid="detail-email-btn"
            className="inline-flex items-center gap-2 rounded-full bg-spott-yellow px-6 py-3.5 text-sm font-bold text-spott-blue">
            <Mail size={16} /> Enviar por correo
          </button>
          <Link to={`/cotizar?retomar=${q.quote_id}`} data-testid="detail-edit-btn"
            className="inline-flex items-center gap-2 rounded-full border border-spott-mid bg-white px-6 py-3.5 text-sm font-bold text-spott-blue">
            <PhoneCall size={16} /> Ajustar campaña
          </Link>
        </div>
      </div>

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent data-testid="detail-email-modal">
          <DialogHeader><DialogTitle className="text-spott-blue">Enviar cotización</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-spott-blue">Correo electrónico</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="detail-email-input" />
            </div>
            <button onClick={send} disabled={busy || !email} data-testid="detail-email-submit"
              className="w-full rounded-full bg-spott-yellow py-3 text-sm font-bold text-spott-blue disabled:opacity-60">
              {busy ? "Enviando…" : "Enviar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
